import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getAffiliationRequest, deleteAffiliation } from 'api/company';
import useHandleSearch from 'utils/customHooks/useHandleSearch';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import CustomTable from 'components/backoffice/customTable';
import DropdownIcons from 'components/backoffice/dropDownIcons';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import Button from 'components/backoffice/buttonCustom';
import Title from 'components/backoffice/texts/title';
import SearchTable from 'components/backoffice/table/SearchTable';
import StatusBudget from 'components/budget/StatusBudget';

const AffiliationBackoffice = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation(['backoffice', 'home']);
  const [inputValue, setInputValue] = useState('');
  const [listAffiliations, setListAffiliations] = useState([]);
  const [page, setPage] = useState(1);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [responseInfo, setResponseInfo] = useState({});
  const [idItem, setIdItem] = useState();
  const { setTitle } = useOutletContext();
  const { loading } = useHandleSearch();

  const colsAffiliations = [
    t('name'),
    t('direccion'),
    t('telefono'),
    t('orderTrans.email'),
    t('requestDescription'),
    t('exports.state'),
    t('actions')
  ];

  const getListAffiliations = async () => {
    try {
      let payload = {
        page,
        page_size: 10,
        name: inputValue,
      };
      const response = await getAffiliationRequest(payload);
      if (response?.data?.results) {
        setListAffiliations(response.data.results);
        setResponseInfo({
          errors: response.errors,
          totalPages: response.data.total_pages,
          noResults: response.data.results.length === 0,
        });
      } else {
        setListAffiliations([]);
        setResponseInfo({ errors: "No data found" });
      }
    } catch (error) {
      toast.error(`${t('error')}: ${t('mensajeerror')}`);
    }
  };

  useEffect(() => {
    setTitle(t('affiliationRequest'));
    return () => {
      setTitle('');
    };
  }, [setTitle]);

  useEffect(() => {
    getListAffiliations();
  }, [page, inputValue]);

  const handleClear = () => {
    setInputValue('');
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }));
  };

  const handleCreate = () => navigate('/intranet/affiliation-request/create');
  const handleEdit = (id) => navigate(`/intranet/affiliation-request/edit/${id}`);

  const handleShowModalDelete = (item, id, setOpenModal) => {
    if (item.id === id) {
      setOpenModal(true);
      setIdItem(id);
    }
  };

  const handleDelete = useCallback(async () => {
    try {
      const { status } = await deleteAffiliation(idItem);
      if (status === 204) {
        toast.success(t('eliminadocorrectamente'));
        getListAffiliations();
      } else {
        toast(`${t('error')} ${status}: ${t('mensajeerror')}`);
      }
    } catch (error) {
      console.error(error);
      toast.error(`${t('error')}: ${t('mensajeerror')}`);
    }
  }, [idItem]);

  const serializerAffiliationStatus = (status) => {
    if (status === 'pending') {
      return 'warning';
    } else if (status === 'accepted') {
      return 'success';
    }
    return 'danger';
  };

  const validateStatusAffiliation = (status) => {
    if (status === 'pending') {
      return t('pending', { ns: 'home' });
    } else if (status === 'accepted') {
      return t('accepted', { ns: 'home' });
    } else {
      return t('rejected', { ns: 'home' });
    }
  };

  return (
    <div className="d-lg-grid">
      <Title text={t('affiliationRequest')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col md={5} xs={12} className="d-flex text-start">
          <SearchTable
            value={inputValue}
            handleSearch={setInputValue}
            handleClear={handleClear}
            placeholder={t('searchByName')}
          />
        </Col>
        <Col md={7} xs={12} className="text-end mt-3 mt-md-0">
          {userPermissions.includes('empresa.__add') && (
            <Button color="primary" onClick={handleCreate}>
              {t('crear')}
            </Button>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsAffiliations}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {listAffiliations?.map((affiliation, index) => (
            <tr key={affiliation.id}>
              <td>{affiliation.name}</td>
              <td>{affiliation.address}</td>
              <td>{affiliation.phone}</td>
              <td>{affiliation.email}</td>
              <td>{affiliation.request_description || t('N/A')}</td>
              <td>
                <div className="d-flex justify-content-center">
                  <StatusBudget
                    text={validateStatusAffiliation(affiliation.status)}
                    status={serializerAffiliationStatus(affiliation.status)}
                  />
                </div>
              </td>
              <td className="item-dropdown text-end">
                <DropdownIcons
                  idItem={index}
                  handleEdit={
                    userPermissions.includes('empresa.__modify') &&
                    (() => handleEdit(affiliation.id))
                  }
                  handleDelete={
                    userPermissions.includes('empresa.__delete') &&
                    (() => handleShowModalDelete(affiliation, affiliation.id, setShowModalDelete))
                  }
                />
              </td>
            </tr>
          ))}
        </CustomTable>
      </div>
      <DeleteModalCustom
        openModal={showModalDelete}
        setOpenModal={setShowModalDelete}
        handleDelete={handleDelete}
        deleteTitle={t('deleteAffiliation')}
        beforeTitle={t('affiliations')}
      />
    </div>
  );
};

export default AffiliationBackoffice;